import React from 'react'
import { Subscribe } from 'unstated'
import CartContainer from '../react/CartContainer'

// Cloud Function in afa-orders (us-west2), next to the gift card database.
const BALANCE_ENDPOINT = process.env.REACT_APP_GIFTCARD_BALANCE_ENDPOINT ||
  'https://us-west2-afa-orders.cloudfunctions.net/giftcardBalance'

// A code arriving as ?giftcard=XXXX-XXXX-XXXX-XXXX (the QR on the card) is stashed here,
// because the customer lands on the site long before reaching checkout.
const PENDING_KEY = 'afa-pending-giftcard'

const REASONS = {
  'not-found': "We couldn't find that gift card.",
  'wrong-restaurant': 'That gift card is not valid at this location.',
}

export const readPendingGiftCardCode = () => {
  try {
    return window.localStorage.getItem(PENDING_KEY) || ''
  } catch (e) { return '' }
}

export const clearPendingGiftCardCode = () => {
  try { window.localStorage.removeItem(PENDING_KEY) } catch (e) {}
}

// Called once on app start (src/index.js).
export const captureGiftCardFromUrl = () => {
  try {
    const code = new URLSearchParams(window.location.search).get('giftcard')
    if (code) { window.localStorage.setItem(PENDING_KEY, code) }
  } catch (e) {}
}

class GiftCard extends React.Component {
  state = {
    code: '',
    checking: false,
    error: null,
    autoTried: false,
  }

  componentDidMount() {
    const pending = readPendingGiftCardCode()
    if (pending && !this.props.cart.state.giftCard) {
      this.setState({ code: pending, autoTried: true }, () => this.check({ silent: true }))
    }
  }

  check = async ({ silent = false } = {}) => {
    const code = this.state.code.trim()
    if (!code) return
    this.setState({ checking: true, error: null })
    try {
      const response = await fetch(BALANCE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, r_id: this.props.shopId }),
      })
      const data = await response.json()
      if (data && data.ok && data.valid) {
        this.props.cart.applyGiftCard({ code: data.code, balanceCents: data.balanceCents })
        clearPendingGiftCardCode()
        this.setState({ checking: false, error: null })
        return
      }
      const reason = (data && data.reason) || (data && data.error)
      // Always say why, even for a code that arrived via the QR link: a silent failure
      // left the customer looking at an empty tab. The code stays in the field.
      this.setState({
        checking: false,
        error: REASONS[reason] || 'That gift card could not be used right now.',
      })
      // Wrong location is the one failure that switching location fixes, so the pending
      // code is kept and auto-applies at the next location. Anything else won't improve
      // by retrying, so stop retrying it on every checkout.
      if (silent && reason !== 'wrong-restaurant') { clearPendingGiftCardCode() }
    } catch (e) {
      console.log('gift card check failed', e)
      this.setState({ checking: false, error: 'Could not check that gift card. Please try again.' })
    }
  }

  onSubmit = (event) => {
    event.preventDefault()
    this.check()
  }

  remove = () => {
    this.props.cart.removeGiftCard()
    clearPendingGiftCardCode()
    this.setState({ code: '', error: null })
  }

  render() {
    const cart = this.props.cart
    const applied = cart.state.giftCard
    const totals = cart.getSubtotals()

    if (applied) {
      const remainingCents = Math.max(0, applied.balanceCents - totals.giftCardCents)
      return (
        <div className="checkout__gift-card">
          <h2 className="instruction">Gift Card</h2>
          <div className="gift-card__applied">
            Gift card applied: −${(totals.giftCardCents / 100).toFixed(2)}
            <button type="button" className="gift-card__remove-button" onClick={this.remove}>remove</button>
          </div>
          {remainingCents > 0 &&
            <div className="gift-card__remaining">
              ${(remainingCents / 100).toFixed(2)} stays on the card for next time.
            </div>}
        </div>
      )
    }

    return (
      <div className="checkout__gift-card">
        <form onSubmit={this.onSubmit}>
          <h2 className="instruction"><label htmlFor="gift-card-code">Gift Card</label></h2>
          <input
            id="gift-card-code"
            type="text"
            name="giftCardCode"
            autoComplete="off"
            autoCapitalize="characters"
            placeholder="XXXX-XXXX-XXXX-XXXX"
            value={this.state.code}
            onChange={(e) => this.setState({ code: e.target.value, error: null })}
          />
          <button type="submit" disabled={this.state.checking || !this.state.code.trim()}>
            {this.state.checking ? 'checking…' : 'apply'}
          </button>
        </form>
        {this.state.error && <div className="gift-card__error" role="alert">{this.state.error}</div>}
      </div>
    )
  }
}

const WrappedGiftCard = (props) => (
  <Subscribe to={[CartContainer]}>
    {(cart) => <GiftCard {...props} cart={cart} />}
  </Subscribe>
)

export default WrappedGiftCard
